import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button, Dropdown, Form } from 'react-bootstrap';
import { BiServer } from 'react-icons/bi';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import AppContext from 'Utils/AppContext';
import {
  getSubset, listSubsetRuns, listTargetGroups, startSubsetProvision, listSubsetProvisions,
  cancelProvision, deleteProvision, errorText,
} from '../tdmApi';
import {
  engineBadge, engineLabel, fmtWhen, fmtNum, rowsLabel, unitNoun, isMaskedSubset, WRITE_MODES, RUN_STATUS_TONE,
  targetWriteScope, TDM_TARGET_RULES,
} from '../tdmUtils';
import SourcePicker from 'DataSources/SourcePicker';
import RunStepStrip from '../RunStepStrip';
import SectionTitle from '../SectionTitle';
import GateNote from '../GateNote';
import usePolling from '../usePolling';
import {
  connectorsForRun, assignmentsFromRun, eligibleTargets, assignConnection, assignTargetGroup, provisionableRuns, newestProvisionable, subsetSources,
} from './provisionUtils';
import { useConfirm } from 'Utils/ConfirmDialog';
import Breadcrumbs from 'Utils/Breadcrumbs';

const POLL_MS = 5000;
const ACTIVE = ['PENDING', 'RUNNING'];
const DEFAULT_OPTIONS = { writeMode: 'UPSERT', disableConstraints: true, failOpen: false };

const newestFirst = (list) => [...(list || [])].sort((a, b) => Number(b.id) - Number(a.id));
const isActive = (r) => ACTIVE.includes(r && r.status);

const optionsFromRun = (p) => (p ? {
  writeMode: WRITE_MODES.some(([v]) => v === p.writeMode) ? p.writeMode : 'UPSERT',
  disableConstraints: p.disableConstraints !== false,
  failOpen: Boolean(p.failOpen),
} : DEFAULT_OPTIONS);

const targetLine = (t) => `${t.dataSourceName}${t.targetGroupName ? ` (via ${t.targetGroupName})` : ''}`;

// Deploy screen (routes and API still say provision): pick a staged export of this subset, say which connection receives each of
// its sources (one at a time, or a whole target group at once), and run it. The history of
// provision runs sits below. Everything the user chooses lives in this page's state — nothing is
// persisted until Deploy now.
const ProvisionScreen = () => {
  const { confirm, confirmDialog } = useConfirm();
  const { id } = useParams();
  const { connections, isAdmin } = useContext(AppContext);

  const [subset, setSubset] = useState(null);
  const [runs, setRuns] = useState([]);
  const [provisions, setProvisions] = useState([]);
  const [targetGroups, setTargetGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [runId, setRunId] = useState('');
  const [selectedSlug, setSelectedSlug] = useState(null);
  // The picker is hidden until a source or its target box is clicked, and again once every source has a target.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [assignments, setAssignments] = useState({});
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [groupError, setGroupError] = useState(null);
  const prefilled = useRef(false);

  const load = useCallback(async () => {
    try {
      const [s, r, p, g] = await Promise.all([
        getSubset(id), listSubsetRuns(id), listSubsetProvisions(id), listTargetGroups().catch(() => []),
      ]);
      const runList = Array.isArray(r) ? r : [];
      const provList = newestFirst(Array.isArray(p) ? p : []);
      setSubset(s);
      setRuns(runList);
      setProvisions(provList);
      setTargetGroups(Array.isArray(g) ? g : []);
      setError(null);
      // The default run and the pre-filled assignments are set ONCE — a reload (poll, after a
      // provision) must not clobber what the user has picked since.
      if (!prefilled.current) {
        prefilled.current = true;
        const first = newestProvisionable(runList);
        setRunId(first ? String(first.id) : '');
        const last = provList[0] || null;
        setAssignments(assignmentsFromRun(last, connectorsForRun(first, subsetSources(s))));
        setOptions(optionsFromRun(last));
      }
    } catch (err) {
      setError(errorText(err, 'Could not load the subset'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const refreshProvisions = useCallback(async () => {
    try {
      const p = await listSubsetProvisions(id);
      setProvisions(newestFirst(Array.isArray(p) ? p : []));
    } catch (err) {
      // A failed poll is not worth an alert — the next tick retries.
    }
  }, [id]);

  const anyActive = provisions.some(isActive);
  usePolling(refreshProvisions, POLL_MS, anyActive);

  const provisionable = useMemo(() => provisionableRuns(runs), [runs]);
  const run = provisionable.find((r) => String(r.id) === String(runId)) || null;
  const connectors = useMemo(() => connectorsForRun(run, subsetSources(subset)), [run, subset]);
  const assignedCount = connectors.filter((c) => assignments[c.slug]).length;
  const eligible = useMemo(
    () => eligibleTargets({ connectors, selectedSlug, connections, targetGroups }),
    [connectors, selectedSlug, connections, targetGroups],
  );
  // The shared picker's restrictions: a writable TDM connection, then the selected source's engine
  // (eligibleTargets decides; a row it leaves out after the TDM rules is an engine mismatch).
  const targetRules = useMemo(() => {
    const ok = new Set(eligible.connections.map((c) => String(c.id)));
    const forSlug = connectors.find((c) => c.slug === selectedSlug);
    const engines = forSlug ? String(forSlug.engine || '').toUpperCase()
      : [...new Set(connectors.map((c) => String(c.engine || '').toUpperCase()))].join(' / ');
    return [...TDM_TARGET_RULES, {
      label: `Engine: ${engines || '—'}`,
      test: (c) => ok.has(String(c.id)),
      why: (c) => `${engineLabel(c)}, not ${engines || 'a staged engine'}`,
    }];
  }, [eligible.connections, selectedSlug, connectors]);
  const masked = isMaskedSubset(subset);
  const name = (subset && subset.name) || 'subset';
  const canAct = isAdmin && !busy && !anyActive && assignedCount > 0 && Boolean(run);
  const selected = connectors.find((c) => c.slug === selectedSlug) || null;
  const engineHeader = selected
    ? `for ${selected.dataSourceName} (${String(selected.engine || '').toUpperCase() || '—'})`
    : `for any source (${[...new Set(connectors.map((c) => engineBadge(c.engine)))].join(' · ') || '—'})`;

  const unassign = (slug) => {
    setAssignments((a) => { const n = { ...a }; delete n[slug]; return n; });
    setSelectedSlug(slug);
    setPickerOpen(true);
  };
  const pick = (slug) => { setSelectedSlug((cur) => (cur === slug ? null : slug)); setPickerOpen(true); };
  const showPicker = pickerOpen && connectors.length > 0 && assignedCount < connectors.length;
  const connById = useMemo(() => Object.fromEntries((connections || []).map((c) => [String(c.id), c])), [connections]);
  const assign = (conn) => {
    setGroupError(null);
    setAssignments((a) => assignConnection(a, connectors, selectedSlug, conn));
  };
  const applyGroup = (group) => {
    const res = assignTargetGroup(assignments, connectors, group);
    setGroupError(res.error || null);
    if (!res.error) setAssignments(res.assignments);
  };
  const setOption = (k, v) => setOptions((o) => ({ ...o, [k]: v }));

  const provision = async (dryRun) => {
    if (!run) return;
    setBusy(true);
    setNotice(null);
    const body = {
      subsetRunId: Number(runId),
      dryRun,
      failOpen: Boolean(options.failOpen),
      writeMode: options.writeMode,
      disableConstraints: Boolean(options.disableConstraints),
      targets: connectors.filter((c) => assignments[c.slug]).map((c) => {
        const a = assignments[c.slug];
        return {
          sourceSlug: c.slug,
          dataSourceId: a.dataSourceId,
          targetGroupId: a.targetGroupId == null ? null : a.targetGroupId,
        };
      }),
    };
    try {
      await startSubsetProvision(id, body);
      await refreshProvisions();
      setNotice({ tone: 'success', text: dryRun ? 'Dry run started — nothing will be written.' : 'Deploy started.' });
    } catch (err) {
      setNotice({ tone: 'danger', text: errorText(err, 'Could not start the deploy') });
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (provId) => {
    try {
      await cancelProvision(provId);
      await refreshProvisions();
    } catch (err) {
      setNotice({ tone: 'danger', text: errorText(err, 'Cancel failed') });
    }
  };

  const removeRun = async (provId) => {
    if (!await confirm({ title: `Delete deploy run ${provId}?`, body: 'Rows already loaded into the targets are not removed.', confirmLabel: 'Delete', variant: 'danger' })) return;
    try {
      await deleteProvision(provId);
      await refreshProvisions();
    } catch (err) {
      setNotice({ tone: 'danger', text: errorText(err, 'Delete failed') });
    }
  };

  return (
    <FetchContainer fetchingData={loading} absolute={true} error={error}>
      <div className='tdm-page'>
      {confirmDialog}
        <Breadcrumbs items={[{ label: 'Test Data Landscape', to: '/tdm' }, { label: name, to: `/tdm/subsets/${id}` }, { label: 'Deploy' }]} />
        <PageHeader
          icon={BiServer}
          eyebrow='Test Data Management / Deploy'
          header={`Deploy ${name}`}
          tooltip='Load a staged export of this subset into test connections'
          meta={<span>
            {connectors.length} source{connectors.length === 1 ? '' : 's'} · {assignedCount} assigned · {masked ? 'masked' : 'raw'}
          </span>}
          primaryAction={
            <Button size='sm' onClick={() => provision(false)} disabled={!canAct} data-testid='tdm-provision-now'>
              {anyActive ? 'Deploying…' : 'Deploy now'}
            </Button>}
          actions={[
            <Dropdown.Item key='dry' data-testid='tdm-provision-dry' onClick={() => provision(true)} disabled={!canAct}>
              Dry run (validate, write nothing)
            </Dropdown.Item>,
          ]}
        />

        {notice && <Alert variant={notice.tone} className='py-1 px-2 mb-2' data-testid='tdm-provision-notice'>{notice.text}</Alert>}
        {!isAdmin && <div className='tdm-note mb-2' data-testid='tdm-provision-admin-note'>Only administrators can deploy.</div>}

        {/* 1. Which export */}
        <div className='tdm-section'>
          <SectionTitle title='Export to deploy' help='Only completed exports whose masking succeeded can be loaded.' />
          {provisionable.length > 0 ? (
            <Form.Control as='select' size='sm' className='tdm-run-picker' value={runId} onChange={(e) => setRunId(e.target.value)}
              data-testid='tdm-run-picker'>
              {provisionable.map((r) => (
                <option key={r.id} value={String(r.id)}>
                  {`${fmtWhen(r.startedAt)} · ${rowsLabel(r, subset && subset.kind) || '—'} · ${masked ? 'masked' : 'raw'}`}
                </option>
              ))}
            </Form.Control>
          ) : (
            // Why earlier exports cannot be deployed is the subset's business (its runs table);
            // here there is only something to load or nothing.
            <div className='tdm-empty' data-testid='tdm-no-run'>No export ready.</div>
          )}
        </div>

        {/* 2. Original → target, one row per source, outside any panel; the picker below fills the selected (or first open) row */}
        <div className='tdm-mirror' data-testid='tdm-mirror'>
            <SectionTitle title='Original' help='The sources this export staged; click one to pick its target, parent first.' />
            <span />
            <SectionTitle title='Target' help='Each source is loaded into its own connection of the same engine. Pick one from the lists below.' />
            {connectors.length === 0 && <><div className='tdm-empty'>No sources.</div><span /><span /></>}
            {connectors.map((c) => {
              const on = c.slug === selectedSlug;
              const a = assignments[c.slug];
              return (
                <React.Fragment key={c.slug}>
                  <button type='button'
                    className={`tdm-connector${on ? ' tdm-connector--on' : ''}`}
                    data-testid={`tdm-connector-${c.slug}`} aria-pressed={on}
                    onClick={() => pick(c.slug)}>
                    <span className={`tdm-badge${c.parent ? ' tdm-badge--parent' : ''}`}>{engineBadge(c.engine)}</span>
                    <span>
                      <span className='tdm-list-item__name'>{c.dataSourceName}</span>
                      {c.orphan && <span className='tdm-pill tdm-pill--warning ml-1'>not in subset</span>}
                      <div className='tdm-list-item__sub'>
                        <span className='tdm-mono'>{c.slug}</span>
                        {' · '}{String(c.engine || '').toUpperCase() || '—'}
                        {c.rows != null && ` · ${fmtNum(c.rows)} ${unitNoun(subset && subset.kind)}`}
                      </div>
                    </span>
                  </button>
                  <span className='tdm-mirror__arrow' aria-hidden='true'>→</span>
                  {a ? (
                    <div className={`tdm-target-box tdm-target-box--on${on ? ' tdm-target-box--selected' : ''}`} data-testid={`tdm-assignment-${c.slug}`}
                      role='button' tabIndex={0} onClick={() => pick(c.slug)}
                      onKeyDown={(e) => {
                        if (e.target !== e.currentTarget) return;   // typing in the schema field is not a pick
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(c.slug); }
                      }}>
                      <span className='tdm-badge'>{engineBadge(a.engine || c.engine)}</span>
                      <span>
                        <span className='tdm-list-item__name'>{targetLine(a)}</span>
                        <div className='tdm-list-item__sub'>
                          {String(a.engine || c.engine || '').toUpperCase() || '—'}
                          {(() => {
                            // Where the rows land is the target connection's own schema (a Mongo
                            // target's database; nothing for Salesforce) — shown, not chosen.
                            const scope = targetWriteScope(a.engine || c.engine, connById[String(a.dataSourceId)]);
                            if (!scope) return null;
                            return (
                              <>
                                {` · ${scope.noun} `}
                                <span className='tdm-mono' data-testid={`tdm-schema-value-${c.slug}`}
                                  title={scope.value ? `Set on the ${targetLine(a)} connection` : `The ${targetLine(a)} connection names no ${scope.noun}: each table goes to the one it was staged from`}>
                                  {scope.value || 'same as source'}
                                </span>
                              </>
                            );
                          })()}
                        </div>
                      </span>
                      {isAdmin && (
                        <button type='button' className='tdm-target-box__unassign' title='Remove target'
                          data-testid={`tdm-connector-unassign-${c.slug}`} onClick={(e) => { e.stopPropagation(); unassign(c.slug); }}>×</button>
                      )}
                    </div>
                  ) : (
                    <button type='button' className={`tdm-target-box tdm-target-box--blank${on ? ' tdm-target-box--waiting' : ''}`}
                      data-testid={`tdm-target-blank-${c.slug}`} onClick={() => pick(c.slug)}>
                      {on ? 'Pick a target below' : 'No target yet'}
                    </button>
                  )}
                </React.Fragment>
              );
            })}
        </div>
        {assignedCount === 0 && connectors.length > 0 && (
          <div className='tdm-note mb-2' data-testid='tdm-no-assignments'>Click a source or its target box, then pick a target.</div>
        )}

        {/* 3. The picker: its own panel, shown only while a target is being chosen */}
        {showPicker && (
        <div className='tdm-section' data-testid='tdm-target-picker'>
          <div className='tdm-section__title'>Select target <span className='tdm-note d-inline'>{engineHeader}</span></div>
          {groupError && <Alert variant='warning' className='py-1 px-2 my-2' data-testid='tdm-provision-error'>{groupError}</Alert>}

          {eligible.groups.length > 0 && (
            <>
              <div className='tdm-note mb-1'>Target groups</div>
              <div className='tdm-table tdm-table--target-pick mb-2'>
                <div className='tdm-table__head'><span>Group</span><span>Members</span><span>Description</span><span /><span /></div>
                {eligible.groups.map((g) => (
                  <div className='tdm-table__row' key={g.id} data-testid={`tdm-target-group-${g.id}`}>
                    <span className='tdm-clip'>{g.name}</span>
                    <span className='tdm-badges'>{(g.members || []).map((m) => <span className='tdm-badge' key={m.id || m.dataSourceId} title={m.dataSourceName}>{engineBadge(m.engine)}</span>)}</span>
                    <span className='tdm-clip'>{g.description || ''}</span>
                    <span className='tdm-note'>{(g.members || []).length} member{(g.members || []).length === 1 ? '' : 's'}</span>
                    <Button size='sm' variant='outline-secondary' className='tdm-btn-xs' disabled={!isAdmin}
                      onClick={() => applyGroup(g)} data-testid={`tdm-use-group-${g.id}`}>Use group</Button>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className='tdm-note mb-1'>Connections</div>
          {eligible.connections.length === 0 && eligible.groups.length === 0 ? (
            <div className='tdm-empty' data-testid='tdm-no-eligible'>No eligible target for this engine.</div>
          ) : (
            // Re-mounted per selected source so a pick for one source never carries to the next.
            <SourcePicker
              key={selectedSlug || 'any'}
              sources={connections || []}
              restrict={targetRules}
              verb='Assign'
              emptyMessage='No eligible connection for this engine.'
              maxHeight={300}
              confirmDisabledReason={isAdmin ? null : 'Only administrators can deploy.'}
              onConfirm={(rows) => assign(rows[0])}
              testId='tdm-target'
            />
          )}
        </div>
        )}

        {/* 4. Options */}
        <div className='tdm-section'>
          <button type='button' className='tdm-options-toggle' onClick={() => setOptionsOpen((o) => !o)}
            aria-expanded={optionsOpen} data-testid='tdm-options-toggle'>
            {optionsOpen ? '▾' : '▸'} Options
          </button>
          <div className='tdm-note' data-testid='tdm-input-folder'>
            {masked ? 'Loads data/ (masked)' : 'Loads data/ (no masking)'}
            {' · '}{options.writeMode === 'UPSERT' ? 'upsert' : 'append'}
            {options.disableConstraints ? ' · foreign keys relaxed' : ''}
            {options.failOpen ? ' · budget overrun allowed' : ''}
          </div>
          {optionsOpen && (
            <div className='mt-2 text-left' data-testid='tdm-options'>
              <Form.Group>
                <Form.Label>Write mode</Form.Label>
                <Form.Control as='select' size='sm' className='tdm-run-picker' value={options.writeMode}
                  onChange={(e) => setOption('writeMode', e.target.value)} data-testid='tdm-env-writemode'>
                  {WRITE_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Form.Control>
                <Form.Text className='text-muted'>Deploying never truncates a target — rows are added or updated.</Form.Text>
              </Form.Group>
              <Form.Check type='checkbox' id='tdm-env-constraints' label='Relax foreign keys during the load'
                checked={Boolean(options.disableConstraints)} onChange={(e) => setOption('disableConstraints', e.target.checked)}
                data-testid='tdm-env-constraints' />
              <Form.Check type='checkbox' id='tdm-env-failopen' label='Deploy even when the budget was exceeded'
                checked={Boolean(options.failOpen)} onChange={(e) => setOption('failOpen', e.target.checked)} data-testid='tdm-env-failopen' />
              <Form.Text className='text-muted'>Masking is never relaxed: a run whose masking failed cannot be deployed.</Form.Text>
            </div>
          )}
        </div>

        {/* 5. History */}
        <div className='tdm-section'>
          <SectionTitle title={`Deploy runs · ${provisions.length}`} help='Gate → one deploy per target. A dry run validates and counts without writing.' />
          {provisions.length === 0 && <div className='tdm-empty'>Not deployed yet.</div>}
          {provisions.map((r) => (
            <div className={`tdm-card${isActive(r) ? ' tdm-card--running' : ''}`} key={r.id} data-testid={`tdm-provision-${r.id}`}>
              <div className='tdm-card__head'>
                <span className='tdm-card__name'>Run {r.id}{r.dryRun ? ' · dry run' : ''}</span>
                <span className={`tdm-pill tdm-pill--${RUN_STATUS_TONE[r.status] || 'neutral'}`}>{r.status}</span>
              </div>
              <RunStepStrip steps={r.steps} />
              <GateNote step={(r.steps || []).find((s) => s.stepType === 'GATE')} />
              <div className='tdm-card__meta'>
                <span>Started</span><span>{fmtWhen(r.startedAt)}</span>
                <span>Staged run</span><span className='tdm-mono'>{r.runPath || '—'}</span>
                {r.totals && <><span>Rows loaded</span><span>{fmtNum(r.totals.rowsLoaded)}{r.totals.rowsFailed ? ` · ${fmtNum(r.totals.rowsFailed)} failed` : ''}</span></>}
                {r.error && <><span>Error</span><span className='tdm-error'>{r.error}</span></>}
              </div>
              {(r.targets || []).length > 0 && (
                <div className='tdm-table tdm-table--run-targets mt-2'>
                  <div className='tdm-table__head'><span>Source</span><span>Target</span><span>Schema</span></div>
                  {r.targets.map((t) => (
                    <div className='tdm-table__row' key={t.id || t.sourceSlug} data-testid={`tdm-provision-${r.id}-target-${t.sourceSlug}`}>
                      <span className='tdm-mono'>{t.sourceSlug}</span>
                      <span><span className='tdm-badge'>{engineBadge(t.engine)}</span> → {targetLine(t)}
                        {t.readOnly && <span className='tdm-pill tdm-pill--danger ml-1'>read only</span>}</span>
                      <span className='tdm-mono'>{targetWriteScope(t.engine) ? (t.targetSchema || 'same as source') : '—'}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className='tdm-card__actions'>
                {isActive(r) && (
                  <Button size='sm' variant='outline-secondary' className='tdm-btn-xs' onClick={() => cancel(r.id)} data-testid={`tdm-provision-cancel-${r.id}`}>
                    {r.cancelRequested ? 'Force cancel' : 'Cancel'}
                  </Button>
                )}
                {!isActive(r) && isAdmin && (
                  <Button size='sm' variant='outline-danger' className='tdm-btn-xs' onClick={() => removeRun(r.id)} data-testid={`tdm-provision-delete-${r.id}`}>Delete</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </FetchContainer>
  );
};

export default ProvisionScreen;
